import {
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import Input from "../../../../../../components/Input";
import { useRelationOptions } from "../../../../../../hooks/useRelationOptions";
import theme from "../../../../../../theme";
import {
  OperationsDetailField,
  OperationsDetailGrid,
} from "../../../../ifas/components/OperationsUI";

const TITLE_OPTIONS = ["Adv", "Dr", "Miss", "Mr", "Mrs", "Ms", "Prof", "Rev"];
const GENDER_OPTIONS = ["Male", "Female", "Other"];

export default function DeceasedDetails({
  deceasedData,
  isEditable = false,
  formData,
  onChange,
}: any) {
  const { relationOptions, loading } = useRelationOptions();
  const inputBackgroundColor = isEditable ? "#FFFFFF" : "background.paper";
  const todayLocal = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];

  const value = (field: string, fallbackField?: string) =>
    isEditable
      ? formData?.[field] || ""
      : deceasedData?.[fallbackField || field] || "";

  const renderMenuItems = (options: string[]) =>
    options.map((option) => (
      <MenuItem
        key={option}
        value={option}
        sx={{
          background: "background.default",
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        {option}
      </MenuItem>
    ));

  if (!deceasedData) {
    return <Typography>Deceased details are not available</Typography>;
  }

  if (!isEditable) {
    return (
      <OperationsDetailGrid>
        <OperationsDetailField label="Title" value={value("title")} />
        <OperationsDetailField label="Initials" value={value("initials")} />
        <OperationsDetailField
          label="First Name"
          value={value("firstName", "first_name")}
        />
        <OperationsDetailField
          label="Last Name"
          value={value("lastName", "last_name")}
        />
        <OperationsDetailField
          label="Identity Type"
          value={value("identificationType", "identification_type")}
        />
        <OperationsDetailField
          label="Identity Number"
          value={value("identificationNumber", "identification_number")}
        />
        <OperationsDetailField label="Gender" value={value("gender")} />
        <OperationsDetailField
          label="Date of Birth"
          value={value("dateOfBirth", "date_of_birth")}
        />
        <OperationsDetailField
          label="Date of Death"
          value={value("dateOfDeath", "date_of_death")}
        />
        <OperationsDetailField
          label="Cause of Death"
          value={value("causeOfDeath", "cause_of_death")}
        />
        <OperationsDetailField
          label="Relationship to Informant"
          value={value("relationship")}
        />
      </OperationsDetailGrid>
    );
  }

  return (
    <Grid
      container
      rowSpacing={5}
      columnSpacing={10}
      mt={1}
      textAlign="left"
      p={3}
    >
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={value("title")}
          onChange={(e: any) => onChange?.({ title: e.target.value })}
          height={40}
          width="100%"
          label="Title"
          backgroundColor={inputBackgroundColor}
        >
          {renderMenuItems(TITLE_OPTIONS)}
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={value("initials")}
          onChange={(e: any) => onChange?.({ initials: e.target.value })}
          height={40}
          width="100%"
          label="Initials"
          backgroundColor={inputBackgroundColor}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={value("firstName", "first_name")}
          onChange={(e: any) => onChange?.({ firstName: e.target.value })}
          height={40}
          width="100%"
          label="First Name*"
          backgroundColor={inputBackgroundColor}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={value("lastName", "last_name")}
          onChange={(e: any) => onChange?.({ lastName: e.target.value })}
          height={40}
          width="100%"
          label="Last Name*"
          backgroundColor={inputBackgroundColor}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        {isEditable ? (
          <RadioGroup
            row
            name="deceased-identification-type"
            value={value("identificationType", "identification_type")}
            onChange={(e) =>
              onChange?.({ identificationType: e.target.value })
            }
          >
            <FormControlLabel value="id" control={<Radio />} label="ID" />
            <FormControlLabel
              value="passport"
              control={<Radio />}
              label="Passport"
            />
          </RadioGroup>
        ) : (
          <Input
            disabled
            value={value("identificationType", "identification_type")}
            height={40}
            width="100%"
            label="Identity Type*"
            backgroundColor={inputBackgroundColor}
          />
        )}
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={value("identificationNumber", "identification_number")}
          onChange={(e: any) =>
            onChange?.({ identificationNumber: e.target.value })
          }
          height={40}
          width="100%"
          label="Identity Number*"
          backgroundColor={inputBackgroundColor}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={value("gender")}
          onChange={(e: any) => onChange?.({ gender: e.target.value })}
          height={40}
          width="100%"
          label="Gender"
          backgroundColor={inputBackgroundColor}
        >
          {renderMenuItems(GENDER_OPTIONS)}
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          type="date"
          value={value("dateOfBirth", "date_of_birth")}
          onChange={(e: any) => onChange?.({ dateOfBirth: e.target.value })}
          height={40}
          width="100%"
          label="Date of Birth"
          backgroundColor={inputBackgroundColor}
          inputProps={{
            max: todayLocal,
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          type="date"
          value={value("dateOfDeath", "date_of_death")}
          onChange={(e: any) => onChange?.({ dateOfDeath: e.target.value })}
          height={40}
          width="100%"
          label="Date of Death"
          backgroundColor={inputBackgroundColor}
          inputProps={{
            max: todayLocal,
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={value("causeOfDeath", "cause_of_death")}
          onChange={(e: any) => onChange?.({ causeOfDeath: e.target.value })}
          height={40}
          width="100%"
          label="Cause Of Death"
          backgroundColor={inputBackgroundColor}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={value("relationship")}
          onChange={(e: any) => onChange?.({ relationship: e.target.value })}
          height={40}
          width="100%"
          label="Relationship to Informant"
          backgroundColor={inputBackgroundColor}
        >
          {loading ? (
            <MenuItem disabled>Loading relationships...</MenuItem>
          ) : (
            renderMenuItems(relationOptions)
          )}
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Grid />
      </Grid>
    </Grid>
  );
}
